import { Injectable, inject } from '@angular/core';

import {Observable, from, throwError} from 'rxjs';
import {TenantService} from './tenant.service';
import {catchError, concatMap, debounceTime, last, map, tap} from 'rxjs/operators';
import {MessagesService} from '../shared/services/messages.service';
import {LoadingService} from '../shared/services/loading.service';
import {Tenant} from '../models/tenant.model';
import { createFirestoreId } from '../common/firestore-utils';
import { Firestore } from '@angular/fire/firestore';
import {isLifetimePlanV2} from '../common/appsumo-utils';
import { Storage, ref, uploadBytesResumable, UploadMetadata, UploadTask, getDownloadURL, deleteObject, UploadTaskSnapshot } from '@angular/fire/storage';

import { selectTenantInfo } from '../store/selectors';
import { select, Store } from '@ngrx/store';
import { AppState } from '../store';

const CACHE_CONTROL_SETTINGS = 'public,max-age=25920000, s-maxage=25920000';

@Injectable()
export class FileUploadService {
  private storage = inject(Storage);
  private tenantService = inject(TenantService);
  private loading = inject(LoadingService);
  private messages = inject(MessagesService);
  private firestore = inject(Firestore);
  private store = inject<Store<AppState>>(Store);

  ref(storagePath: string) {
    throw new Error("Method not implemented.");
  }

  tenant:Tenant;

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {

    this.store.pipe(select(selectTenantInfo),).subscribe(tenant => this.tenant = tenant as Tenant);

  }

  uploadFile(file: File, filePath: string, randomFileName = false, addThumbPrefix = false): UploadTask {

    this.checkIfStorageSpaceExceeded(file);

    let uploadPath;

    if (addThumbPrefix) {
      uploadPath = filePath + '/thumb_' + file.name;
    } else {
      uploadPath = filePath + '/' + file.name;
    }

    if (randomFileName) {

      const extension = file.name.split('.').pop();

      uploadPath = uploadPath.replace(extension, createFirestoreId(this.firestore) + "." + extension);

    }

    return uploadBytesResumable(ref(this.storage, uploadPath), file, {cacheControl: CACHE_CONTROL_SETTINGS});
  }

  uploadFileWithNewName(file: File, filePath: string, fileName: string, downloadFileName: string = null): UploadTask {


  const fullPath = `${filePath}/${fileName}`;
  console.log('Firebase storage path:', fullPath);

  const finalDownloadName = downloadFileName ? downloadFileName : fileName;
  console.log('Download file name to be set:', finalDownloadName);

  try {
    this.checkIfStorageSpaceExceeded(file);
  } catch (err) {
    console.error('Storage limit error:', err);
    throw err;
  }

  const task = uploadBytesResumable(
    ref(this.storage, fullPath),
    file,
    {
      cacheControl: CACHE_CONTROL_SETTINGS,
      contentDisposition: `attachment; filename=${finalDownloadName};`,
    }
  );

  console.log(task,'Upload task created successfully');
  return task;
}


  uploadProtectedResource(courseId: string, lessonId: string, file: File, fileName: string = null): UploadTask {

    this.checkIfStorageSpaceExceeded(file);

    const uploadFileName = fileName ? fileName : this.generateUrlSafeUploadFileName(file);

    const uploadPath = `${this.tenantService.id}/${courseId}/protectedResources/${lessonId}/${uploadFileName}`;

    return uploadBytesResumable(
      ref(this.storage, uploadPath),
      file, {
        cacheControl: CACHE_CONTROL_SETTINGS,
        contentDisposition: `attachment; filename=${file.name};`
      });

  }

  generateUrlSafeUploadFileName(file: File) {

    const extension = file.name.split('.').pop();

    return `${createFirestoreId(this.firestore)}.${extension}`;

  }

  getDownloadUrl(filePath: string): Observable<string> {
    return from(getDownloadURL(ref(this.storage, filePath)))
    // return this.storage.ref(filePath).getDownloadURL();
  }

  deleteFile(filePath: string) {
    return from(deleteObject(ref(this.storage, filePath)))
    // return this.storage.ref(filePath).delete();
  }

  uploadAndCreateUrl(storagePath: string, file: File, attachment = false) {

    this.checkIfStorageSpaceExceeded(file);

    const headers: UploadMetadata = {
      cacheControl: CACHE_CONTROL_SETTINGS
    };

    if (attachment) {
      headers.contentDisposition = `attachment; filename=${file.name};`
    }

    const uploadTask = uploadBytesResumable(ref(this.storage,`${storagePath}/${file.name}`), file, headers);

    const percentageUpload$ = this.percentageChanges(uploadTask);

    const upload$ = this.loading.showLoaderUntilCompleted(percentageUpload$);

    return upload$
      .pipe(
        last(),
        catchError(err => {
          console.log("Error uploading file, reason: ", err);
          this.messages.error('Could not upload file.');
          return throwError(err);
        }),
        concatMap(() => this.getDownloadUrl(`${storagePath}/${file.name}`)),
        catchError(err => {
          console.log("Error creating download url, reason: ", err);
          this.messages.error('File uploaded, but could not create download url.');
          return throwError(err);
        }),
      )
  }

  private checkIfStorageSpaceExceeded(file: File) {

    // storage space only applies to AppSumo lifetime users with version 2 plan
    // version 3 does not have a storage limit
    if (!isLifetimePlanV2(this.tenant)) {
      return false;
    }

    const maxStorageInMb = (this.tenant?.appsumoTierLimits?.maxStorage as number) * 1024;

    const currentStorageInMb = this.tenant?.storageUsedInMb as number;

    const fileSizeInMb = file.size / 1024 / 1024;

    if (currentStorageInMb + fileSizeInMb > maxStorageInMb) {

      const maxStorageInGb = (maxStorageInMb / 1024).toFixed(2);

      this.messages.error(`You have exceeded your storage limit of ${maxStorageInGb} GB. Go to Admin / General to check your storage.`);

      throw new Error('Max storage send-limit-email exceeded.');

    }

  }

  percentageChanges(uploadTask: UploadTask): Observable<number> {
    const inner$ = fromTask(uploadTask);
    return inner$.pipe(map((s) => (s.bytesTransferred / s.totalBytes) * 100))
  }

  uploadTaskToObservable(uploadTask: UploadTask) {
    const inner$ = fromTask(uploadTask);
    return inner$

  }

  snapshotChanges(uploadTask: UploadTask) {
    return this.uploadTaskToObservable(uploadTask)
  }
}

function fromTask(task: UploadTask) {
  return new Observable<UploadTaskSnapshot>((subscriber) => {
    const progress = (snap: UploadTaskSnapshot) => {
      subscriber.next(snap);
    };

    const error = (e) => {
      console.log("Error in upload task:", JSON.stringify(e));
      console.error("error", e);
      subscriber.error(e);
    };

    const complete = () => {
      console.log("Upload task completed successfully.");
      subscriber.complete();
    };

    progress(task.snapshot);

    // const unsub = task.on("state_changed", progress);
    const unsub = task.on("state_changed", progress, error, complete);

    // task.then(
    //   (snapshot) => {
    //     progress(snapshot);
    //     complete();
    //   },
    //   (e) => {
    //
    //     progress(task?.snapshot);
    //     error(e);
    //   }
    // );
    return function unsubscribe() {
      unsub();
    };
  }).pipe(
    tap(() => console.log("Upload task progress:")),
    debounceTime(50),
    // timeout({
    //   each: 10000, // Max 10 seconds between progress events
    //   with: () => throwError(() => new Error('Upload stalled: no progress detected'))
    // }),
    catchError(err => {
      return throwError(err);
    })
  );
}
